import { ReportComponentType } from '@/types';

export const EMBED_REPORT_COMPONENT_TYPES = [
    ReportComponentType.EMBED,
    ReportComponentType.YOUTUBE,
    ReportComponentType.LOOM,
    ReportComponentType.VIMEO,
    ReportComponentType.GOOGLE_CALENDAR,
    ReportComponentType.MICROSOFT_OUTLOOK_CALENDAR,
    ReportComponentType.GOOGLE_DRIVE,
    ReportComponentType.GOOGLE_DOCS,
    ReportComponentType.GOOGLE_SHEETS,
    ReportComponentType.MICROSOFT_ONEDRIVE,
    ReportComponentType.DROPBOX,
    ReportComponentType.BOX,
    ReportComponentType.NOTION,
    ReportComponentType.GITHUB,
    ReportComponentType.HEX,
    ReportComponentType.CONFLUENCE,
    ReportComponentType.MIRO,
    ReportComponentType.LUCID,
    ReportComponentType.GRAPHY,
    ReportComponentType.MICROSOFT_POWER_BI,
    ReportComponentType.LOOKER,
    ReportComponentType.TABLEAU,
    ReportComponentType.ZOHO_ANALYTICS,
] as const;

type EmbedRule = {
    example: string;
    hint: string;
    isValid: (url: URL) => boolean;
    normalize?: (url: URL) => string;
};

const hostMatches = (url: URL, ...hosts: string[]) =>
    hosts.some((host) => url.hostname === host || url.hostname.endsWith(`.${host}`));

const googlePreviewUrl = (url: URL) => {
    const normalized = new URL(url.toString());

    normalized.pathname = normalized.pathname.replace(/\/(edit|view)(?:\/.*)?$/, '/preview');
    normalized.search = '';

    return normalized.toString();
};

const RULES: Partial<Record<ReportComponentType, EmbedRule>> = {
    [ReportComponentType.YOUTUBE]: {
        example: 'https://www.youtube.com/watch?v=VIDEO_ID',
        hint: 'Use a YouTube watch, share, or embed URL.',
        isValid: (url) => hostMatches(url, 'youtube.com', 'youtu.be'),
        normalize: (url) => {
            const videoId = url.hostname.endsWith('youtu.be')
                ? url.pathname.split('/').filter(Boolean)[0]
                : url.pathname.startsWith('/embed/')
                  ? url.pathname.split('/')[2]
                  : url.searchParams.get('v');

            return videoId ? `https://www.youtube.com/embed/${videoId}` : url.toString();
        },
    },
    [ReportComponentType.LOOM]: {
        example: 'https://www.loom.com/share/VIDEO_ID',
        hint: 'Use a Loom share or embed URL.',
        isValid: (url) => hostMatches(url, 'loom.com') && /^\/(share|embed)\//.test(url.pathname),
        normalize: (url) => url.toString().replace('/share/', '/embed/'),
    },
    [ReportComponentType.VIMEO]: {
        example: 'https://vimeo.com/436840030',
        hint: 'Use a public Vimeo video or player URL.',
        isValid: (url) => hostMatches(url, 'vimeo.com') && /\/\d+/.test(url.pathname),
        normalize: (url) => {
            const videoId = url.pathname.match(/\d+/)?.[0];

            return videoId ? `https://player.vimeo.com/video/${videoId}` : url.toString();
        },
    },
    [ReportComponentType.GOOGLE_CALENDAR]: {
        example: 'https://calendar.google.com/calendar/embed?src=...',
        hint: 'Use the embed URL from a Google Calendar sharing configuration.',
        isValid: (url) => hostMatches(url, 'calendar.google.com') && url.pathname.includes('/calendar/embed'),
    },
    [ReportComponentType.MICROSOFT_OUTLOOK_CALENDAR]: {
        example: 'https://outlook.live.com/owa/calendar/.../index.html',
        hint: 'Use the published Outlook calendar HTML link.',
        isValid: (url) =>
            hostMatches(url, 'outlook.live.com', 'outlook.office.com') && url.pathname.includes('/calendar/'),
    },
    [ReportComponentType.GOOGLE_DRIVE]: {
        example: 'https://drive.google.com/file/d/FILE_ID/view',
        hint: 'The Drive file must be shared with everyone who can view this Report.',
        isValid: (url) => hostMatches(url, 'drive.google.com') && /\/file\/d\/[^/]+\/(view|preview)/.test(url.pathname),
        normalize: googlePreviewUrl,
    },
    [ReportComponentType.GOOGLE_DOCS]: {
        example: 'https://docs.google.com/document/d/DOCUMENT_ID/edit',
        hint: 'Use a Google Docs document URL with access enabled for Report viewers.',
        isValid: (url) =>
            hostMatches(url, 'docs.google.com') && /\/document\/d\/[^/]+\/(edit|preview)/.test(url.pathname),
        normalize: googlePreviewUrl,
    },
    [ReportComponentType.GOOGLE_SHEETS]: {
        example: 'https://docs.google.com/spreadsheets/d/SHEET_ID/edit',
        hint: 'Use a Google Sheets URL with access enabled for Report viewers.',
        isValid: (url) =>
            hostMatches(url, 'docs.google.com') && /\/spreadsheets\/d\/[^/]+\/(edit|preview)/.test(url.pathname),
        normalize: googlePreviewUrl,
    },
    [ReportComponentType.MICROSOFT_ONEDRIVE]: {
        example: 'https://1drv.ms/...',
        hint: 'Use a public OneDrive or SharePoint sharing link.',
        isValid: (url) => hostMatches(url, '1drv.ms', 'onedrive.live.com', 'sharepoint.com'),
    },
    [ReportComponentType.DROPBOX]: {
        example: 'https://www.dropbox.com/scl/fi/...',
        hint: 'Use a Dropbox sharing link that Report viewers can access.',
        isValid: (url) => hostMatches(url, 'dropbox.com'),
    },
    [ReportComponentType.BOX]: {
        example: 'https://app.box.com/embed/s/...',
        hint: 'Use a Box embed or shared file URL.',
        isValid: (url) => hostMatches(url, 'box.com'),
    },
    [ReportComponentType.NOTION]: {
        example: 'https://workspace.notion.site/ebd/PAGE_ID',
        hint: 'The Notion page must be published and embeddable.',
        isValid: (url) => hostMatches(url, 'notion.site', 'notion.so'),
    },
    [ReportComponentType.GITHUB]: {
        example: 'https://gist.github.com/user/GIST_ID.pibb',
        hint: 'GitHub Gist embed links must end in .pibb.',
        isValid: (url) => hostMatches(url, 'gist.github.com') && url.pathname.endsWith('.pibb'),
    },
    [ReportComponentType.HEX]: {
        example: 'https://app.hex.tech/.../latest?embedded=true',
        hint: 'Use the embedded app URL generated by Hex.',
        isValid: (url) => hostMatches(url, 'hex.tech') && url.searchParams.get('embedded') === 'true',
    },
    [ReportComponentType.CONFLUENCE]: {
        example: 'https://workspace.atlassian.net/wiki/...',
        hint: 'Use a published Confluence page that Report viewers can access.',
        isValid: (url) => hostMatches(url, 'atlassian.net') && url.pathname.includes('/wiki/'),
    },
    [ReportComponentType.MIRO]: {
        example: 'https://miro.com/app/live-embed/...',
        hint: 'Use the view-only live embed URL generated by Miro.',
        isValid: (url) => hostMatches(url, 'miro.com') && url.pathname.includes('/app/live-embed/'),
    },
    [ReportComponentType.LUCID]: {
        example: 'https://lucid.app/documents/embedded/DOCUMENT_ID',
        hint: 'Use the embedded document URL generated by Lucid.',
        isValid: (url) => hostMatches(url, 'lucid.app') && url.pathname.includes('/documents/embedded/'),
    },
    [ReportComponentType.GRAPHY]: {
        example: 'https://visualize.graphy.app/view/CHART_ID',
        hint: 'Use a published Graphy visualization URL.',
        isValid: (url) => hostMatches(url, 'graphy.app') && url.pathname.includes('/view/'),
    },
    [ReportComponentType.MICROSOFT_POWER_BI]: {
        example: 'https://app.powerbi.com/view?r=...',
        hint: 'Use a Power BI publish-to-web or secure embed URL.',
        isValid: (url) => hostMatches(url, 'powerbi.com'),
    },
    [ReportComponentType.LOOKER]: {
        example: 'https://lookerstudio.google.com/embed/reporting/...',
        hint: 'Use the Looker Studio embed URL, not the editor URL.',
        isValid: (url) => hostMatches(url, 'lookerstudio.google.com') && url.pathname.includes('/embed/'),
    },
    [ReportComponentType.TABLEAU]: {
        example: 'https://public.tableau.com/views/...?:showVizHome=no',
        hint: 'Use a Tableau share URL with Show Viz Home disabled.',
        isValid: (url) => hostMatches(url, 'tableau.com') && url.pathname.includes('/views/'),
    },
    [ReportComponentType.ZOHO_ANALYTICS]: {
        example: 'https://analytics.zoho.com/open-view/...',
        hint: 'Use the permalink or embed URL generated by Zoho Analytics.',
        isValid: (url) => hostMatches(url, 'zoho.com') && /\/(open-view|embed)\//.test(url.pathname),
    },
};

export const getEmbedUrlGuidance = (type: ReportComponentType) =>
    RULES[type] ?? {
        example: 'https://example.com/embed',
        hint: 'Use an HTTPS URL from a provider that permits iframe embedding.',
    };

export const validateReportComponentEmbedUrl = (type: ReportComponentType, value?: string) => {
    const input = value?.trim();

    if (!input) {
        return { isValid: false, message: 'Enter an embed URL.' };
    }

    let url: URL;

    try {
        url = new URL(input);
    } catch {
        return { isValid: false, message: 'Enter a complete, valid URL.' };
    }

    if (url.protocol !== 'https:') {
        return { isValid: false, message: 'Use a secure HTTPS URL.' };
    }

    const rule = RULES[type];

    if (rule && !rule.isValid(url)) {
        return { isValid: false, message: `This is not a valid ${type} embed URL.` };
    }

    return {
        isValid: true,
        message: undefined,
        normalizedUrl: rule?.normalize?.(url) ?? url.toString(),
    };
};
